import React from "react";
/**
 * Botón con estado de carga (spinner + deshabilitado) y microinteracción al clic.
 * Variants: primary | secondary | danger
 */
type ButtonVariant = "primary" | "secondary" | "danger" | "outline" | "ghost" | "link" | "text" | "disabled";
export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "size"> & {
    loading?: boolean;
    loadingLabel?: string;
    variant?: ButtonVariant;
    /** Tamaño visual (clases en app); no se envía como atributo HTML */
    size?: string;
};
export default function Button({ type = "button", disabled = false, loading = false, loadingLabel, variant = "primary", className = "", children, size: _size, ...props }: ButtonProps): any {
    const base = "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] cursor-pointer";
    const variants = {
        primary: "bg-brand-700 text-fg-on-brand hover:bg-brand-800 focus:ring-brand-500",
        secondary: "border border-border-strong text-fg-muted bg-surface hover:bg-surface-sunken focus:ring-border-strong",
        danger: "bg-danger text-fg-on-brand hover:opacity-90 focus:ring-danger",
        outline: "border border-border-strong text-fg-muted bg-surface hover:bg-surface-sunken focus:ring-border-strong",
        ghost: "bg-transparent text-fg-muted hover:bg-surface-sunken focus:ring-border-strong",
        link: "bg-transparent text-fg-muted hover:bg-surface-sunken focus:ring-border-strong",
        text: "bg-transparent text-fg-muted hover:bg-surface-sunken focus:ring-border-strong",
        disabled: "opacity-50 cursor-not-allowed",
    };
    const v = variants[variant] ?? variants.primary;
    // Heurística mínima: si el botón dice "Guardar..." mostramos "Guardando..." durante la carga.
    const isSaveLabel = typeof children === "string" && /^Guardar(\s|$)/.test(children);
    const label = loadingLabel ?? (isSaveLabel ? "Guardando..." : "Cargando...");
    return (<button type={type} disabled={disabled || loading} className={`${base} ${v} ${className}`} {...props}>
      {loading ? (<>
          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0"/>
          <span>{label}</span>
        </>) : (children)}
    </button>);
}
